import React, { useEffect, useState } from "react";
import axios from "axios";

const API_BASE = process.env.REACT_APP_API_BASE_URL;
const LANGS = ["En", "Ar", "Ku"];

const VoyahDreamSpecificationsEditor = () => {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);

  // GROUP MODAL
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [groupForm, setGroupForm] = useState({
    id: null,
    order: 1,
    type: "table",
    titleEn: "",
    titleAr: "",
    titleKu: "",
  });

  // ROW MODAL
  const [showRowModal, setShowRowModal] = useState(false);
  const [rowForm, setRowForm] = useState({
    id: null,
    groupId: null,
    order: 1,
    labelEn: "",
    labelAr: "",
    labelKu: "",
    valueEn: "",
    valueAr: "",
    valueKu: "",
  });

  // IMAGE MODAL
  const [showImageModal, setShowImageModal] = useState(false);
  const [imageForm, setImageForm] = useState({
    id: null,
    groupId: null,
    order: 1,
    labelEn: "",
    labelAr: "",
    labelKu: "",
    imageFile: null,
    existingImageUrl: null,
  });

  // ============================================================
  // LOAD DATA (ADMIN)
  // ============================================================
  useEffect(() => {
    const load = async () => {
      try {
        const token = localStorage.getItem("token");

        const res = await axios.get(`${API_BASE}/api/VoyahDreamSpec/admin`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        setGroups(res.data);
        setLoading(false);
      } catch (err) {
        console.error(err);
      }
    };

    load();
  }, []);

  // ============================================================
  // GROUP CRUD
  // ============================================================
  const openNewGroup = () => {
    setGroupForm({
      id: null,
      order: 1,
      type: "table",
      titleEn: "",
      titleAr: "",
      titleKu: "",
    });
    setShowGroupModal(true);
  };

  const openEditGroup = (g) => {
    setGroupForm({
      id: g.id,
      order: g.order,
      type: g.type,
      titleEn: g.titleEn,
      titleAr: g.titleAr,
      titleKu: g.titleKu,
    });
    setShowGroupModal(true);
  };

  const saveGroup = async () => {
    const token = localStorage.getItem("token");

    try {
      if (groupForm.id === null) {
        // CREATE
        await axios.post(
          `${API_BASE}/api/VoyahDreamSpec/group`,
          {
            order: groupForm.order,
            type: groupForm.type,
            titleEn: groupForm.titleEn,
            titleAr: groupForm.titleAr,
            titleKu: groupForm.titleKu,
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } else {
        // UPDATE
        await axios.put(
          `${API_BASE}/api/VoyahDreamSpec/group/${groupForm.id}`,
          {
            order: groupForm.order,
            type: groupForm.type,
            titleEn: groupForm.titleEn,
            titleAr: groupForm.titleAr,
            titleKu: groupForm.titleKu,
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }

      window.location.reload();
    } catch (err) {
      console.error(err);
      alert("Failed to save group.");
    }
  };

  const deleteGroup = async (id) => {
    if (!window.confirm("Delete this group?")) return;
    const token = localStorage.getItem("token");

    try {
      await axios.delete(`${API_BASE}/api/VoyahDreamSpec/group/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      window.location.reload();
    } catch (err) {
      console.error(err);
      alert("Delete failed.");
    }
  };

  // ============================================================
  // ROW CRUD
  // ============================================================
  const openNewRow = (groupId) => {
    setRowForm({
      id: null,
      groupId,
      order: 1,
      labelEn: "",
      labelAr: "",
      labelKu: "",
      valueEn: "",
      valueAr: "",
      valueKu: "",
    });
    setShowRowModal(true);
  };

  const openEditRow = (r) => {
    setRowForm({
      id: r.id,
      groupId: r.groupId,
      order: r.order,
      labelEn: r.labelEn,
      labelAr: r.labelAr,
      labelKu: r.labelKu,
      valueEn: r.valueEn,
      valueAr: r.valueAr,
      valueKu: r.valueKu,
    });
    setShowRowModal(true);
  };

  const saveRow = async () => {
    const token = localStorage.getItem("token");

    try {
      if (rowForm.id === null) {
        await axios.post(`${API_BASE}/api/VoyahDreamSpec/row`, rowForm, {
          headers: { Authorization: `Bearer ${token}` },
        });
      } else {
        await axios.put(
          `${API_BASE}/api/VoyahDreamSpec/row/${rowForm.id}`,
          rowForm,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }

      window.location.reload();
    } catch (err) {
      console.error(err);
      alert("Failed to save row.");
    }
  };

  const deleteRow = async (id) => {
    if (!window.confirm("Delete this row?")) return;
    const token = localStorage.getItem("token");

    try {
      await axios.delete(`${API_BASE}/api/VoyahDreamSpec/row/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      window.location.reload();
    } catch (err) {
      console.error(err);
      alert("Delete failed.");
    }
  };

  // ============================================================
  // IMAGE CRUD
  // ============================================================
  const openNewImage = (groupId) => {
    setImageForm({
      id: null,
      groupId,
      order: 1,
      labelEn: "",
      labelAr: "",
      labelKu: "",
      imageFile: null,
      existingImageUrl: null,
    });
    setShowImageModal(true);
  };

  const openEditImage = (img) => {
    setImageForm({
      id: img.id,
      groupId: img.groupId,
      order: img.order,
      labelEn: img.labelEn,
      labelAr: img.labelAr,
      labelKu: img.labelKu,
      imageFile: null,
      existingImageUrl: img.imageUrl,
    });
    setShowImageModal(true);
  };

  const saveImage = async () => {
    const token = localStorage.getItem("token");
    const fd = new FormData();

    fd.append("GroupId", imageForm.groupId);
    fd.append("Order", imageForm.order);
    fd.append("LabelEn", imageForm.labelEn || "");
    fd.append("LabelAr", imageForm.labelAr || "");
    fd.append("LabelKu", imageForm.labelKu || "");

    if (imageForm.imageFile) {
      fd.append("Image", imageForm.imageFile);
    }

    try {
      if (imageForm.id === null) {
        await axios.post(`${API_BASE}/api/VoyahDreamSpec/image`, fd, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        });
      } else {
        await axios.put(
          `${API_BASE}/api/VoyahDreamSpec/image/${imageForm.id}`,
          fd,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "multipart/form-data",
            },
          }
        );
      }

      window.location.reload();
    } catch (err) {
      console.error(err);
      alert("Failed to save image.");
    }
  };

  const deleteImage = async (id) => {
    if (!window.confirm("Delete this image?")) return;

    const token = localStorage.getItem("token");

    try {
      await axios.delete(`${API_BASE}/api/VoyahDreamSpec/image/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      window.location.reload();
    } catch (err) {
      console.error(err);
      alert("Delete failed.");
    }
  };

  // ============================================================
  // RENDER
  // ============================================================

  if (loading) return <p>Loading...</p>;

  return (
    <div className="container py-4">
      <h2 className="fw-bold mb-4">VOYAH DREAM — Specifications Editor</h2>
        <p className="alert alert-info">
        When adding or editing a <strong>Group</strong>, <strong>Row</strong>, or <strong>Image</strong>, 
        you must fill in <strong>all language fields</strong> (EN, AR, KU).  
        Leaving any field empty will cause the specification to not work correctly on the website.
        </p>
      {/* GROUP LIST */}
      {groups.map((g) => (
        <div className="card mb-4 shadow-sm" key={g.id}>
          <div className="card-body">
            <h4 className="mb-3">
              Group #{g.id} — {g.titleEn} ({g.type})
            </h4>

            {/* TABLE GROUP */}
            {g.type === "table" && (
              <>
                <button
                  className="btn btn-primary btn-sm mb-3"
                  onClick={() => openNewRow(g.id)}
                >
                  + Add Table Row
                </button>

                <table className="table table-striped">
                  <thead>
                    <tr>
                      <th>Order</th>
                      <th>Label (EN)</th>
                      <th>Value (EN)</th>
                      <th style={{ width: "180px" }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {g.rows.map((r) => (
                      <tr key={r.id}>
                        <td>{r.order}</td>
                        <td>{r.labelEn}</td>
                        <td>{r.valueEn}</td>
                        <td>
                          <button
                            className="btn btn-warning btn-sm me-2"
                            onClick={() => openEditRow(r)}
                          >
                            Edit
                          </button>
                          <button
                            className="btn btn-danger btn-sm"
                            onClick={() => deleteRow(r.id)}
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}

            {/* IMAGE GROUP */}
            {g.type === "images" && (
              <>
                <button
                  className="btn btn-primary btn-sm mb-3"
                  onClick={() => openNewImage(g.id)}
                >
                  + Add Image
                </button>

                <div className="row">
                  {g.images.map((img) => (
                    <div className="col-md-4 mb-3" key={img.id}>
                      <div className="border p-2 rounded shadow-sm">
                        {img.imageUrl && (
                          <img
                            src={`${API_BASE}${img.imageUrl}`}
                            alt=""
                            className="img-fluid rounded mb-2"
                          />
                        )}
                        <p className="fw-bold">{img.labelEn}</p>

                        <button
                          className="btn btn-warning btn-sm me-2"
                          onClick={() => openEditImage(img)}
                        >
                          Edit
                        </button>

                        <button
                          className="btn btn-danger btn-sm"
                          onClick={() => deleteImage(img.id)}
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}

            {/* GROUP ACTION BUTTONS */}
            <div className="d-flex gap-2 mt-3">
              <button
                className="btn btn-warning"
                onClick={() => openEditGroup(g)}
              >
                Edit Group
              </button>

              <button
                className="btn btn-danger"
                onClick={() => deleteGroup(g.id)}
              >
                Delete Group
              </button>
            </div>
          </div>
        </div>
      ))}

      {/* ADD GROUP BUTTON */}
      <button className="btn btn-success mt-4" onClick={openNewGroup}>
        + Add New Group
      </button>

      {/* ========== GROUP MODAL ========== */}
      {showGroupModal && (
        <>
          <div className="modal fade show" style={{ display: "block" }}>
            <div className="modal-dialog">
              <div className="modal-content shadow">
                <div className="modal-header">
                  <h5 className="modal-title">
                    {groupForm.id === null ? "Add Group" : "Edit Group"}
                  </h5>
                  <button
                    className="btn-close"
                    onClick={() => setShowGroupModal(false)}
                  />
                </div>

                <div className="modal-body">
                  <label className="form-label">Order</label>
                  <input
                    className="form-control mb-3"
                    type="number"
                    value={groupForm.order}
                    onChange={(e) =>
                      setGroupForm({
                        ...groupForm,
                        order: Number(e.target.value),
                      })
                    }
                  />

                  <label className="form-label">Group Type</label>
                  <select
                    className="form-control mb-3"
                    value={groupForm.type}
                    onChange={(e) =>
                      setGroupForm({ ...groupForm, type: e.target.value })
                    }
                  >
                    <option value="table">Table</option>
                    <option value="images">Images</option>
                  </select>

                  {LANGS.map((lang) => (
                    <div key={lang} className="mb-3">
                      <label className="form-label">{`Title ${lang}`}</label>
                      <input
                        className="form-control"
                        value={groupForm[`title${lang}`]}
                        onChange={(e) =>
                          setGroupForm({
                            ...groupForm,
                            [`title${lang}`]: e.target.value,
                          })
                        }
                      />
                    </div>
                  ))}
                </div>

                <div className="modal-footer">
                  <button
                    className="btn btn-secondary"
                    onClick={() => setShowGroupModal(false)}
                  >
                    Cancel
                  </button>
                  <button className="btn btn-primary" onClick={saveGroup}>
                    Save Group
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="modal-backdrop fade show" />
        </>
      )}

      {/* ========== ROW MODAL ========== */}
      {showRowModal && (
        <>
          <div className="modal fade show" style={{ display: "block" }}>
            <div className="modal-dialog modal-lg">
              <div className="modal-content shadow">
                <div className="modal-header">
                  <h5 className="modal-title">
                    {rowForm.id === null ? "Add Row" : "Edit Row"}
                  </h5>
                  <button
                    className="btn-close"
                    onClick={() => setShowRowModal(false)}
                  />
                </div>

                <div className="modal-body">
                  <label className="form-label">Order</label>
                  <input
                    className="form-control mb-3"
                    type="number"
                    value={rowForm.order}
                    onChange={(e) =>
                      setRowForm({
                        ...rowForm,
                        order: Number(e.target.value),
                      })
                    }
                  />

                  <h6 className="fw-bold">Labels</h6>
                  {LANGS.map((lang) => (
                    <input
                      key={lang}
                      className="form-control mb-2"
                      placeholder={`Label ${lang}`}
                      value={rowForm[`label${lang}`]}
                      onChange={(e) =>
                        setRowForm({
                          ...rowForm,
                          [`label${lang}`]: e.target.value,
                        })
                      }
                    />
                  ))}

                  <h6 className="fw-bold mt-3">Values</h6>
                  {LANGS.map((lang) => (
                    <input
                      key={lang}
                      className="form-control mb-2"
                      placeholder={`Value ${lang}`}
                      value={rowForm[`value${lang}`]}
                      onChange={(e) =>
                        setRowForm({
                          ...rowForm,
                          [`value${lang}`]: e.target.value,
                        })
                      }
                    />
                  ))}
                </div>

                <div className="modal-footer">
                  <button
                    className="btn btn-secondary"
                    onClick={() => setShowRowModal(false)}
                  >
                    Cancel
                  </button>
                  <button className="btn btn-primary" onClick={saveRow}>
                    Save Row
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="modal-backdrop fade show" />
        </>
      )}

      {/* ========== IMAGE MODAL ========== */}
      {showImageModal && (
        <>
          <div className="modal fade show" style={{ display: "block" }}>
            <div className="modal-dialog modal-lg">
              <div className="modal-content shadow">
                <div className="modal-header">
                  <h5 className="modal-title">
                    {imageForm.id === null ? "Add Image" : "Edit Image"}
                  </h5>
                  <button
                    className="btn-close"
                    onClick={() => setShowImageModal(false)}
                  />
                </div>

                <div className="modal-body">
                  <label className="form-label">Order</label>
                  <input
                    className="form-control mb-3"
                    type="number"
                    value={imageForm.order}
                    onChange={(e) =>
                      setImageForm({
                        ...imageForm,
                        order: Number(e.target.value),
                      })
                    }
                  />

                  <h6 className="fw-bold">Labels</h6>
                  {LANGS.map((lang) => (
                    <input
                      key={lang}
                      className="form-control mb-2"
                      placeholder={`Label ${lang}`}
                      value={imageForm[`label${lang}`]}
                      onChange={(e) =>
                        setImageForm({
                          ...imageForm,
                          [`label${lang}`]: e.target.value,
                        })
                      }
                    />
                  ))}

                  <h6 className="fw-bold mt-3">Image Upload</h6>

                  {imageForm.existingImageUrl && (
                    <img
                      src={`${API_BASE}${imageForm.existingImageUrl}`}
                      alt=""
                      className="img-fluid rounded mb-3"
                      style={{ maxHeight: "180px" }}
                    />
                  )}

                  <input
                    type="file"
                    className="form-control"
                    onChange={(e) =>
                      setImageForm({
                        ...imageForm,
                        imageFile: e.target.files[0],
                      })
                    }
                  />
                </div>

                <div className="modal-footer">
                  <button
                    className="btn btn-secondary"
                    onClick={() => setShowImageModal(false)}
                  >
                    Cancel
                  </button>
                  <button className="btn btn-primary" onClick={saveImage}>
                    Save Image
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="modal-backdrop fade show" />
        </>
      )}
    </div>
  );
};

export default VoyahDreamSpecificationsEditor;
